import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Switch, Typography } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "../../controllers/authController";
import CachedIcon from "@mui/icons-material/Cached";
import useAuthController from "../../hooks/useAuthController";
import CandidateImportExportDialog from '../CandidateImportExportDialog';
import CandidateTransferPermissions from '../CandidateTransferPermissions';

const SYSTEM_CONTROL_ROLES = new Set(["system_user"]);

function preserveAuthAndClearStorage(storage) {
  const preserved = ["token", "user", "role"]
    .map((key) => [key, storage.getItem(key)])
    .filter(([, value]) => value != null);

  storage.clear();
  preserved.forEach(([key, value]) => storage.setItem(key, value));
}

export default function SystemSettings({ section = "all" }) {
  const { user, loading } = useAuthController();
  const [clearing, setClearing] = useState(false);
  const [transferLogOpen, setTransferLogOpen] = useState(false);
  const navigate = useNavigate();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaMandatory, setMfaMandatory] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsMessage, setSettingsMessage] = useState("");
  const isSystemUser = useMemo(
    () => SYSTEM_CONTROL_ROLES.has(String(user?.role || "").trim().toLowerCase()),
    [user?.role]
  );
  const authHeaders = () => ({ Authorization: `Bearer ${(localStorage.getItem("token") || localStorage.getItem("authToken") || sessionStorage.getItem("token") || sessionStorage.getItem("authToken") || "").replace(/^Bearer\s+/i, "")}` });
  const settingsUrl = `${String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "")}/settings/authentication`;
  useEffect(() => {
    if (!isSystemUser || !["all", "security"].includes(section)) return;
    let active = true;
    axios.get(settingsUrl, { headers: authHeaders() }).then(({ data }) => {
      if (active) { setMfaEnabled(data.enabled === true); setMfaMandatory(data.mandatory === true); setSettingsLoaded(true); }
    }).catch(() => { if (active) setSettingsError("Could not load two-factor authentication settings. Reload to try again."); });
    return () => { active = false; };
  }, [isSystemUser, settingsUrl, section]);

  const saveMfa = async () => {
    setSaving(true); setSettingsError(""); setSettingsMessage("");
    try {
      const { data } = await axios.put(settingsUrl, { enabled: mfaEnabled }, { headers: authHeaders() });
      setMfaEnabled(data.enabled);
      if (data.enabled) {
        await logoutUser();
        navigate("/login", { replace: true, state: { message: "Two-factor authentication is enabled. Sign in to set up or verify Google Authenticator." } });
      } else setSettingsMessage("Two-factor authentication is disabled.");
    } catch (error) { setSettingsError(error.response?.data?.error || "Could not save two-factor authentication settings."); }
    finally { setSaving(false); }
  };

  const handleClearApplicationCache = useCallback(async () => {
    setClearing(true);

    try {
      preserveAuthAndClearStorage(window.localStorage);
      preserveAuthAndClearStorage(window.sessionStorage);

      if ("caches" in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
      }

      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } finally {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set("cacheBust", String(Date.now()));
      window.location.replace(nextUrl.toString());
    }
  }, []);

  if (loading) return <p>Loading...</p>;

  return (
    <Box sx={{ "& .MuiCard-root": { borderColor: "#D2D8DE", borderRadius: "8px", boxShadow: "none" }, "& .MuiCardContent-root": { p: { xs: 2, md: 4 } }, "& .MuiTypography-h6": { color: "#3451F4", fontSize: 16 } }}>
      {["all", "audit"].includes(section) && <Card variant="outlined" sx={{ mb: 3 }}><CardContent><Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Candidate Import / Export Audit Log</Typography><Typography color="text.secondary" sx={{ mb: 2 }}>Review your transfer history, resume imports, and download row errors.</Typography><Button variant="outlined" onClick={() => setTransferLogOpen(true)}>View Import / Export Log</Button></CardContent></Card>}
      <CandidateImportExportDialog open={transferLogOpen} onClose={() => setTransferLogOpen(false)} mode="history" />
      {["all", "permissions"].includes(section) && ['admin','administrator','system_user'].includes(user?.role) && <CandidateTransferPermissions />}
      {isSystemUser ? (
        <>
        {["all", "security"].includes(section) && <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Two-factor authentication (2FA)</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Require Google Authenticator verification codes for all administrator and System accounts.
              {mfaMandatory
                ? " Two-factor authentication is required in this environment."
                : " Use this setting to enable or disable two-factor authentication in this environment."}
              {" Enabling this signs you out so you can complete verification. Other administrators must verify when they next sign in."}
            </Typography>
            {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}
            {settingsMessage && <Alert severity="success" sx={{ mb: 2 }}>{settingsMessage}</Alert>}
            <FormControlLabel control={<Switch checked={mfaEnabled} onChange={event => setMfaEnabled(event.target.checked)} disabled={mfaMandatory || !settingsLoaded || saving} />} label="Enable administrator 2FA" />
            {mfaMandatory && <Typography>Administrator two-factor authentication is required.</Typography>}
            <Box><Button variant="contained" onClick={saveMfa} disabled={mfaMandatory || !settingsLoaded || saving}>{saving ? "Saving…" : "Save 2FA setting"}</Button></Box>
          </CardContent>
        </Card>}
        {["all", "maintenance"].includes(section) && <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Application cache
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Clear this browser&apos;s saved application data and reload the latest deployed version. Your
              sign-in session is retained.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Use this after a production deployment when the browser is still showing an older version.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              startIcon={clearing ? <CircularProgress color="inherit" size={18} /> : <CachedIcon />}
              disabled={clearing}
              onClick={handleClearApplicationCache}
            >
              {clearing ? "Clearing cache…" : "Clear cache and reload"}
            </Button>
          </CardContent>
        </Card>}
        </>
      ) : null}
    </Box>
  );
}
